import React, { createContext, useContext } from "react";
import { fitBeam } from "@/lib/beams";

// Small SVG helpers for drawing quavers, crotchets and beamed groups in the treble or bass clef.
const LETTERS = "CDEFGAB";
const OFFSET = { treble: 0, bass: 12 };
export const ClefCtx = createContext("treble");
export const step = (p, clef = "treble") => Number(p.slice(-1)) * 7 + LETTERS.indexOf(p[0]) - 30 + OFFSET[clef]; // bottom line = 0, middle line = 4 (B4 treble, D3 bass)
export const fromMiddle = (p, clef) => step(p, clef) - 4;
export const yOf = (p, base = 108, clef) => base - step(p, clef) * 6;
export const STAVE = [60, 72, 84, 96, 108];
export const CLEF_PAD = 46;
const INK = "currentColor";
// Same stave positions, renamed for the bass clef (treble E4 on the bottom line becomes bass G2).
const down12 = (p) => { const i = Number(p.slice(-1)) * 7 + LETTERS.indexOf(p[0]) - 12; return LETTERS[i % 7] + Math.floor(i / 7); };
export const toClef = (ps, clef) => (clef === "bass" ? ps.map(down12) : ps);
export const viewBox = (x, y, w, h, clef) => (clef === "bass" ? `${x - CLEF_PAD} ${y} ${w + CLEF_PAD} ${h}` : `${x} ${y} ${w} ${h}`);

export const Stave = ({ x1 = 4, x2 = 96 }) => {
  const clef = useContext(ClefCtx), a = clef === "bass" ? x1 - CLEF_PAD : x1;
  return (
    <>
      {STAVE.map((y) => <line key={y} x1={a} x2={x2} y1={y} y2={y} stroke={INK} strokeOpacity="0.45" strokeWidth="1" />)}
      {clef === "bass" && <text x={a + 3} y={72 + 0.645 * 55} fontSize="55" fill={INK} fontFamily="'Noto Music', serif" aria-label="bass clef">{"\u{1D122}"}</text>}
    </>
  );
};

export const Head = ({ x, p }) => {
  const clef = useContext(ClefCtx), y = yOf(p, 108, clef), s = step(p, clef);
  return (
    <>
      {(s <= -2 || s >= 10) && <line x1={x - 11} x2={x + 11} y1={s <= -2 ? 120 : 48} y2={s <= -2 ? 120 : 48} stroke={INK} strokeOpacity="0.7" strokeWidth="1" />}
      <ellipse cx={x} cy={y} rx="7.5" ry="5.4" transform={`rotate(-20 ${x} ${y})`} fill={INK} />
    </>
  );
};

// One note on its own: crotchet (no flag) or single quaver (flag), stem direction from the middle-line rule.
export const Single = ({ x, p, quaver, up: forced }) => {
  const clef = useContext(ClefCtx), up = forced ?? fromMiddle(p, clef) < 0, y = yOf(p, 108, clef), sx = x + (up ? 7 : -7), tip = up ? y - 34 : y + 34;
  return (
    <>
      <Head x={x} p={p} />
      <line x1={sx} y1={y} x2={sx} y2={tip} stroke={INK} strokeWidth="1.8" />
      {quaver && <path d={`M ${sx} ${tip} ${up ? "q 4 10 10 16" : "q 6 -8 10 -18"}`} fill="none" stroke={INK} strokeWidth="2" />}
    </>
  );
};

// A group of quavers joined by one beam, sloped with the notes; `up` picks the stem direction.
export const Beamed = ({ xs, ps, up }) => {
  const clef = useContext(ClefCtx), ys = ps.map((p) => yOf(p, 108, clef));
  const sxs = xs.map((x) => x + (up ? 7 : -7));
  const line = fitBeam(sxs.map((x, i) => ({ x, top: ys[i], bottom: ys[i] })), up, 30, 10);
  const a = sxs[0] - 0.9, z = sxs[sxs.length - 1] + 0.9, t = up ? 5 : -5;
  return (
    <>
      {xs.map((x, i) => <Head key={i} x={x} p={ps[i]} />)}
      {sxs.map((sx, i) => <line key={i} x1={sx} y1={ys[i]} x2={sx} y2={line.at(sx)} stroke={INK} strokeWidth="1.8" />)}
      <polygon points={`${a},${line.at(a)} ${z},${line.at(z)} ${z},${line.at(z) + t} ${a},${line.at(a) + t}`} fill={INK} />
    </>
  );
};

// The textbook rule: the note furthest from the middle line decides; equal distances can go either way.
export const pairDirections = (ps, clef) => {
  const m = (p) => fromMiddle(p, clef);
  const far = ps.reduce((a, p) => (Math.abs(m(p)) > Math.abs(m(a)) ? p : a), ps[0]);
  const tied = ps.some((p) => p !== far && Math.abs(m(p)) === Math.abs(m(far)) && Math.sign(m(p)) !== Math.sign(m(far)));
  if (tied || m(far) === 0) return ["up", "down"];
  return [m(far) < 0 ? "up" : "down"];
};
